/**
 * Transfer Tab
 *
 * Enables transferring Smart Asset units to another address.
 */
import React from "react";
import type { UseTransferStateReturn } from "../../../../hooks";
import type { SmartAssetData } from "../SmartAssetCard";
import { DEMO_CONTACTS } from "../../../../data/demoContacts";

const PARTITION_CONTACTS = DEMO_CONTACTS.filter((contact) => contact.rgbAddress);

interface TransferTabProps {
  asset: SmartAssetData;
  totalUnits: number;
  transferState: UseTransferStateReturn;
}

export const TransferTab: React.FC<TransferTabProps> = ({ asset, totalUnits, transferState }) => {
  const {
    transferUnits,
    recipientType,
    selectedContact,
    manualAddress,
    hasRecipient,
    willPartition,
    transferFee,
    canTransfer,
    setTransferUnits,
    setRecipientType,
    setSelectedContact,
    setManualAddress,
    reset,
  } = transferState;

  // Display helper for recipient
  const recipientDisplay =
    recipientType === "contact"
      ? (PARTITION_CONTACTS.find((c) => c.id === selectedContact)?.name ?? "—")
      : manualAddress || "—";

  return (
    <div
      className="bg-sa-surface border border-sa-border rounded-lg p-6"
      data-testid="smart-asset-transfer-content"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-purple-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Transfer {asset.name} Units</h2>
          <p className="text-sm text-sa-text-muted">
            Send ownership stakes to another SATCHEL or RGB address.
          </p>
        </div>
      </div>

      {/* Source Section */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-sa-text-muted uppercase tracking-wider mb-3">
          Source
        </h3>
        <div className="rounded-xl border border-purple-500/40 bg-sa-surface-hover p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-purple-500">
                Your {asset.ticker} Allocation
              </p>
              <p className="text-xs text-sa-text-muted mt-1">RGB:bc1p_Source...</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-sa-text">{totalUnits}</p>
              <p className="text-xs text-sa-text-muted">Units Available</p>
            </div>
          </div>
        </div>
      </div>

      {/* Amount Section */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-sa-text-muted uppercase tracking-wider mb-3">
          Amount to Transfer
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-sa-text-muted block mb-1">Units</label>
            <input
              type="number"
              min={0}
              max={totalUnits}
              value={transferUnits || ""}
              onChange={(e) => {
                const val = Number(e.target.value);
                setTransferUnits(Number.isNaN(val) ? 0 : Math.min(val, totalUnits));
              }}
              placeholder="0"
              data-testid="smart-asset-transfer-units-input"
              className="w-full rounded-lg border border-sa-border bg-sa-surface px-3 py-2 text-sm text-sa-text"
            />
          </div>
          <div>
            <label className="text-xs text-sa-text-muted block mb-1">Quick Select</label>
            <div className="flex gap-2">
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setTransferUnits(Math.floor((totalUnits * pct) / 100))}
                  data-testid={`smart-asset-transfer-quick-${pct}`}
                  className="flex-1 px-2 py-2 text-xs font-medium rounded-lg border border-sa-border text-sa-text-muted hover:bg-sa-surface-hover transition-colors"
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>
        </div>
        {totalUnits > 0 && (
          <div className="mt-3">
            <input
              type="range"
              min={0}
              max={totalUnits}
              value={transferUnits}
              onChange={(e) => setTransferUnits(Number(e.target.value))}
              data-testid="smart-asset-transfer-slider"
              className="w-full accent-purple-500"
            />
            <div className="flex justify-between text-xs text-sa-text-muted mt-1">
              <span>0 Units</span>
              <span>{transferUnits > 0 ? Math.round((transferUnits / totalUnits) * 100) : 0}%</span>
              <span>{totalUnits} Units</span>
            </div>
          </div>
        )}
        {willPartition && (
          <div className="mt-3 rounded-lg border border-sa-warning/40 bg-sa-warning/10 px-3 py-2">
            <p className="text-xs text-sa-warning">
              <span className="font-semibold">Auto-partition:</span> Transferring {transferUnits} of{" "}
              {totalUnits} units will automatically create a new partition.
            </p>
          </div>
        )}
      </div>

      {/* Recipient Section */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-sa-text-muted uppercase tracking-wider mb-3">
          Recipient
        </h3>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setRecipientType("contact")}
            data-testid="smart-asset-transfer-recipient-contact"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              recipientType === "contact"
                ? "bg-primary text-white"
                : "border border-sa-border text-sa-text-muted hover:bg-sa-surface-hover"
            }`}
          >
            Select Contact
          </button>
          <button
            type="button"
            onClick={() => setRecipientType("manual")}
            data-testid="smart-asset-transfer-recipient-manual"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              recipientType === "manual"
                ? "bg-primary text-white"
                : "border border-sa-border text-sa-text-muted hover:bg-sa-surface-hover"
            }`}
          >
            Enter Address
          </button>
        </div>

        {recipientType === "contact" ? (
          <select
            value={selectedContact}
            onChange={(e) => setSelectedContact(e.target.value)}
            data-testid="smart-asset-transfer-contact-select"
            className="w-full rounded-lg border border-sa-border bg-sa-surface px-3 py-2 text-sm text-sa-text"
          >
            <option value="">Select a contact...</option>
            {PARTITION_CONTACTS.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.name} · {contact.rgbAddress}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={manualAddress}
            onChange={(e) => setManualAddress(e.target.value)}
            placeholder="RGB Address or SATCHEL Handle (e.g., RGB:bc1p... or @username)"
            data-testid="smart-asset-transfer-address-input"
            className="w-full rounded-lg border border-sa-border bg-sa-surface px-3 py-2 text-sm text-sa-text"
          />
        )}
      </div>

      {/* Route Visualization */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-sa-text-muted uppercase tracking-wider mb-3">
          Transfer Route
        </h3>
        <div
          className="rounded-lg border border-sa-border bg-sa-surface-hover p-4"
          data-testid="smart-asset-transfer-route"
        >
          <div className="flex items-center justify-between">
            {/* Source Node */}
            <div className="flex-1 text-center">
              <div className="inline-flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-2">
                  <span className="text-purple-500 font-bold text-sm">You</span>
                </div>
                <p className="text-xs text-sa-text-muted">Your SATCHEL</p>
                <p className="text-sm font-semibold text-sa-text mt-1">
                  {transferUnits > 0 ? `-${transferUnits}` : "0"} Units
                </p>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0 px-4">
              <div className="flex items-center gap-1">
                <div className="w-16 h-0.5 bg-gradient-to-r from-purple-400 to-purple-600" />
                <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                </svg>
              </div>
              <p className="text-xs text-sa-text-dim text-center mt-1">
                {transferUnits} {asset.ticker}
              </p>
            </div>

            {/* Destination Node */}
            <div className="flex-1 text-center">
              <div className="inline-flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    hasRecipient ? "bg-sa-success/10" : "bg-sa-surface-hover"
                  }`}
                >
                  <svg
                    className={`w-5 h-5 ${hasRecipient ? "text-sa-success" : "text-sa-text-dim"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <p className="text-xs text-sa-text-muted">Recipient</p>
                <p className="text-sm font-semibold text-sa-text mt-1 max-w-[120px] truncate">
                  {hasRecipient ? `+${transferUnits} Units` : "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Section */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-sa-text-muted uppercase tracking-wider mb-3">
          Transaction Fee
        </h3>
        <div className="rounded-lg border border-sa-border bg-sa-surface-hover p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Battery indicator */}
              <div className="relative w-10 h-5 rounded-sm border-2 border-sa-border">
                <div className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-1 h-2 bg-sa-text-dim rounded-r-sm" />
                <div
                  className="absolute left-0.5 top-0.5 bottom-0.5 bg-green-500 rounded-sm transition-all"
                  style={{ width: `${Math.max(10, 100 - transferFee * 2)}%` }}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-sa-text">SA Credits (Battery)</p>
                <p className="text-xs text-sa-text-muted">Primary fee source</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1">
                <img src="./assets/smart-assets/PAIR.png" alt="PAIR" className="w-4 h-4" />
                <p className="text-lg font-bold text-sa-text">{transferFee}</p>
              </div>
              <p className="text-xs text-sa-text-muted">Credits</p>
            </div>
          </div>
          {willPartition && (
            <p className="text-xs text-sa-text-muted mt-2 pt-2 border-t border-sa-border">
              Fee includes automatic partition cost (+5 Credits)
            </p>
          )}
        </div>
      </div>

      {/* Review Summary */}
      {canTransfer && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-sa-text-muted uppercase tracking-wider mb-3">
            Review
          </h3>
          <div className="rounded-lg border border-purple-500/40 bg-purple-500/10 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-sa-text-muted">Transferring:</span>
              <span className="font-semibold text-sa-text">
                {transferUnits} {asset.ticker} Units
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-sa-text-muted">To:</span>
              <span className="font-semibold text-sa-text max-w-[200px] truncate">
                {recipientDisplay}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-sa-text-muted">Fee:</span>
              <span className="font-semibold text-sa-text">{transferFee} Credits</span>
            </div>
            {willPartition && (
              <div className="flex justify-between text-sm">
                <span className="text-sa-text-muted">Remaining:</span>
                <span className="font-semibold text-sa-text">
                  {totalUnits - transferUnits} Units
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={reset}
          data-testid="smart-asset-transfer-cancel"
          className="px-4 py-2 text-sm font-medium rounded-lg border border-sa-border text-sa-text-muted hover:bg-sa-surface-hover transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canTransfer}
          aria-disabled={!canTransfer}
          data-testid="smart-asset-transfer-confirm"
          className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-600"
        >
          Sign & Transfer
        </button>
      </div>
    </div>
  );
};

export default TransferTab;
